import { useEffect } from 'preact/hooks';
import { playerSettings, PLAYBACK_RATES, setRate, setAudioStyle } from '../state/player-settings.js';
import { seekRelative, togglePlay } from '../audio/controller.js';

/**
 * Keyboard shortcuts for the reader. Mirrors video-player conventions —
 * space toggles play, left/right skip ±10s, up/down step through speeds,
 * `m` toggles between dramatized and plain audio. Skips when focus is in
 * a text input so verse search / chat input still receive their keys.
 */
export function useKeyboardShortcuts(): void {
  useEffect(() => {
    function isTypingTarget(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
      if (target.isContentEditable) return true;
      return false;
    }

    function onKey(e: KeyboardEvent): void {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;

      switch (e.key) {
        case ' ':
        case 'Spacebar': {
          e.preventDefault();
          void togglePlay();
          return;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          seekRelative(-60);
          return;
        }
        case 'ArrowRight': {
          e.preventDefault();
          seekRelative(60);
          return;
        }
        case 'ArrowUp': {
          e.preventDefault();
          stepRate(1);
          return;
        }
        case 'ArrowDown': {
          e.preventDefault();
          stepRate(-1);
          return;
        }
        case 'j':
        case 'J': {
          seekRelative(-60);
          return;
        }
        case 'l':
        case 'L': {
          seekRelative(60);
          return;
        }
        case 'k':
        case 'K': {
          void togglePlay();
          return;
        }
        case 'm':
        case 'M': {
          const next = playerSettings.value.audioStyle === 'dramatized' ? 'plain' : 'dramatized';
          setAudioStyle(next);
          return;
        }
        default:
          return;
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

function stepRate(direction: 1 | -1): void {
  const current = playerSettings.value.rate;
  // PLAYBACK_RATES is descending [2, 1.75, 1.5, 1.25, 1]; ArrowUp = faster,
  // ArrowDown = slower, so step the index in the opposite direction.
  const idx = PLAYBACK_RATES.findIndex((r) => r === current);
  if (idx === -1) {
    setRate(1);
    return;
  }
  const nextIdx = Math.max(0, Math.min(PLAYBACK_RATES.length - 1, idx - direction));
  const nextRate = PLAYBACK_RATES[nextIdx];
  if (typeof nextRate === 'number') setRate(nextRate);
}
